import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Shield, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const C6_ValidateFramework = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const [validationResults, setValidationResults] = useState([]);

  const criteria = [
    { id: 'clear', name: 'Clear', question: 'Can readers understand without asking questions?', 
      test: () => {
        const constructs = dataC?.constructs || [];
        const withDefinitions = constructs.filter(c => c.definition && c.definition.trim().length > 10);
        return withDefinitions.length >= constructs.length * 0.5;
      }
    },
    { id: 'logical', name: 'Logical', question: 'Do the connections make sense?',
      test: () => {
        const connections = dataC?.diagramConnections || [];
        const nodes = dataC?.diagramNodes || [];
        if (connections.length === 0 || nodes.length < 2) return false;
        const hasDependent = nodes.some(n => n.type === 'dependent');
        const hasIndependent = nodes.some(n => n.type === 'independent');
        return hasDependent && hasIndependent;
      }
    },
    { id: 'parsimonious', name: 'Parsimonious', question: 'Is it as simple as possible?',
      test: () => {
        const constructs = dataC?.constructs || [];
        return constructs.length >= 2 && constructs.length <= 7;
      }
    },
    { id: 'comprehensive', name: 'Comprehensive', question: 'Are all relevant elements included?',
      test: () => {
        const constructs = dataC?.constructs || [];
        const connections = dataC?.diagramConnections || [];
        return constructs.length >= 3 && connections.length >= 2;
      }
    },
    { id: 'testable', name: 'Testable', question: 'Can you collect data to test this?',
      test: () => {
        const propositions = dataC?.propositions || [];
        return propositions.length >= 1;
      }
    },
    { id: 'grounded', name: 'Grounded', question: 'Is it based on theory/evidence?',
      test: () => {
        const assumptions = dataC?.assumptions || {};
        const total = Object.values(assumptions).flat().length;
        return total >= 2;
      }
    },
    { id: 'novel', name: 'Novel', question: 'Does it add something new?',
      test: () => true
    },
    { id: 'useful', name: 'Useful', question: 'Does it help solve real problems?',
      test: () => true
    },
    { id: 'bounded', name: 'Bounded', question: 'Is the scope clear?',
      test: () => {
        const levels = dataC?.levels || [];
        return levels.length >= 1 && levels.length <= 3;
      }
    },
    { id: 'visual', name: 'Visual', question: 'Can you explain it with a picture?',
      test: () => {
        const nodes = dataC?.diagramNodes || [];
        const connections = dataC?.diagramConnections || [];
        return nodes.length >= 2 && connections.length >= 1;
      }
    }
  ];

  useEffect(() => {
    const results = criteria.map(c => ({
      ...c,
      passed: c.test()
    }));
    setValidationResults(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataC?.constructs?.length, dataC?.diagramConnections?.length, dataC?.diagramNodes?.length, dataC?.propositions?.length, dataC?.assumptions, dataC?.levels?.length]);

  const passedCount = validationResults.filter(r => r.passed).length;
  const totalCount = validationResults.length;
  const percentage = Math.round((passedCount / totalCount) * 100);

  const getScoreColor = () => {
    if (percentage >= 80) return '#22c55e';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreLabel = () => {
    if (percentage >= 80) return 'Strong Framework';
    if (percentage >= 60) return 'Needs Improvement';
    return 'Requires Attention';
  };

  const handleContinue = () => {
    updateDataRaw({ 'wizardC.validationResults': validationResults });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon validation-icon">
          <Shield size={32} />
        </div>
        <div>
          <h2>Step 8: Validate Framework</h2>
          <p className="step-description">Check your framework against quality criteria</p>
        </div>
      </div>

      <div className="step-content">
        <div className="validation-score" style={{ borderColor: getScoreColor() }}>
          <div className="score-circle" style={{ background: getScoreColor() }}>
            <span className="score-value">{percentage}%</span>
          </div>
          <div className="score-info">
            <h3 style={{ color: getScoreColor() }}>{getScoreLabel()}</h3>
            <p>{passedCount} of {totalCount} criteria met</p>
          </div>
        </div>

        <div className="validation-criteria">
          {validationResults.map((criterion) => (
            <div 
              key={criterion.id} 
              className={`criterion-card ${criterion.passed ? 'passed' : 'failed'}`}
            >
              <div className="criterion-status">
                {criterion.passed ? (
                  <CheckCircle size={24} className="status-pass" />
                ) : (
                  <XCircle size={24} className="status-fail" />
                )}
              </div>
              <div className="criterion-content">
                <h4>{criterion.name}</h4>
                <p>{criterion.question}</p>
              </div>
              <div className="criterion-badge">
                {criterion.passed ? 'Passed' : 'Needs Work'}
              </div>
            </div>
          ))}
        </div>

        {percentage < 100 && (
          <div className="improvement-suggestions">
            <h4>
              <AlertTriangle size={18} />
              Suggestions for Improvement
            </h4>
            <ul>
              {validationResults.filter(r => !r.passed).map((criterion) => (
                <li key={criterion.id}>
                  <strong>{criterion.name}:</strong> {
                    criterion.id === 'clear' ? 'Add definitions to more constructs' :
                    criterion.id === 'logical' ? 'Ensure you have both independent and dependent variables' :
                    criterion.id === 'parsimonious' ? 'Keep constructs between 2-7 for optimal clarity' :
                    criterion.id === 'comprehensive' ? 'Add more constructs and connections' :
                    criterion.id === 'testable' ? 'Generate at least one proposition' :
                    criterion.id === 'grounded' ? 'Select more assumptions in Step 4' :
                    criterion.id === 'bounded' ? 'Select 1-3 levels of analysis' :
                    criterion.id === 'visual' ? 'Create a diagram with nodes and connections' :
                    'Review this criterion'
                  }
                </li>
              ))}
            </ul>
          </div>
        )}

        {percentage >= 80 && (
          <div className="success-message">
            <CheckCircle size={24} />
            <div>
              <strong>Great job!</strong> Your framework meets most quality criteria. 
              You can proceed to export your diagram and generate the framework section.
            </div>
          </div>
        )}
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={true}
        continueLabel="Continue to Export"
      />
    </div>
  );
};

export default C6_ValidateFramework;
